import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Globe,
  Inbox,
  KeyRound,
  Mail,
  RefreshCw,
  Settings as SettingsIcon,
  ShieldCheck,
} from "lucide-react";

import MailboxService, {
  MailboxOauthConfigInput,
  MailboxOauthPublicConfig,
} from "../../_services/MailboxService";
import "./mailbox.css";

type TabKey = "google" | "microsoft" | "general";

interface FormState {
  googleClientId: string;
  googleClientSecret: string;
  googleRedirectUri: string;
  microsoftClientId: string;
  microsoftClientSecret: string;
  microsoftRedirectUri: string;
  microsoftTenantAuthority: string;
  iteachBaseUrl: string;
}

const EMPTY_FORM: FormState = {
  googleClientId: "",
  googleClientSecret: "",
  googleRedirectUri: "",
  microsoftClientId: "",
  microsoftClientSecret: "",
  microsoftRedirectUri: "",
  microsoftTenantAuthority: "common",
  iteachBaseUrl: "",
};

const MailboxAdminPage: React.FC = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<TabKey>("google");
  const [config, setConfig] = useState<MailboxOauthPublicConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [original, setOriginal] = useState<FormState>(EMPTY_FORM);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    MailboxService.getOauthConfig()
      .then((res) => {
        const cfg = res.data && res.data.config;
        setConfig(cfg || null);
        const next: FormState = {
          googleClientId: cfg?.google.clientId || "",
          googleClientSecret: "",
          googleRedirectUri: cfg?.google.redirectUri || "",
          microsoftClientId: cfg?.microsoft.clientId || "",
          microsoftClientSecret: "",
          microsoftRedirectUri: cfg?.microsoft.redirectUri || "",
          microsoftTenantAuthority: cfg?.microsoft.tenantAuthority || "common",
          iteachBaseUrl: cfg?.iteachBaseUrl || "",
        };
        setForm(next);
        setOriginal(next);
      })
      .catch((err) =>
        setError(
          err?.response?.status === 403
            ? "You need admin privileges to edit OAuth settings."
            : err?.response?.data?.error?.message || err?.message || "Failed to load",
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!info) return;
    const t = setTimeout(() => setInfo(null), 3500);
    return () => clearTimeout(t);
  }, [info]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original]);

  const revert = () => setForm(original);

  const save = async () => {
    setSaving(true);
    setError(null);
    setInfo(null);
    const payload: MailboxOauthConfigInput = {};
    payload.google = {
      clientId: form.googleClientId.trim(),
      redirectUri: form.googleRedirectUri.trim(),
    };
    if (form.googleClientSecret.trim()) {
      payload.google.clientSecret = form.googleClientSecret;
    }
    payload.microsoft = {
      clientId: form.microsoftClientId.trim(),
      redirectUri: form.microsoftRedirectUri.trim(),
      tenantAuthority: form.microsoftTenantAuthority.trim() || "common",
    };
    if (form.microsoftClientSecret.trim()) {
      payload.microsoft.clientSecret = form.microsoftClientSecret;
    }
    if (form.iteachBaseUrl.trim()) {
      payload.iteachBaseUrl = form.iteachBaseUrl.trim();
    }

    try {
      const res = await MailboxService.saveOauthConfig(payload);
      const cfg = res.data && res.data.config;
      setConfig(cfg || null);
      setInfo("OAuth client configuration saved.");
      // Clear secret fields so the placeholder is correct on the next render.
      const next: FormState = {
        ...form,
        googleClientSecret: "",
        microsoftClientSecret: "",
      };
      setForm(next);
      setOriginal(next);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Renders ──────────────────────────────────────────────────────────
  const sourceBanner = () =>
    config && (
      <div className="mbx-source-banner">
        <span className="mbx-source-item">
          <SettingsIcon size={13} /> <strong>Source:</strong> {config.source}
        </span>
        <span className="mbx-source-item">
          {config.google.configured ? (
            <CheckCircle2 size={13} style={{ color: "var(--status-success-text)" }} />
          ) : (
            <AlertCircle size={13} style={{ color: "var(--status-warning-text)" }} />
          )}
          Google {config.google.configured ? "configured" : "not configured"}
        </span>
        <span className="mbx-source-item">
          {config.microsoft.configured ? (
            <CheckCircle2 size={13} style={{ color: "var(--status-success-text)" }} />
          ) : (
            <AlertCircle size={13} style={{ color: "var(--status-warning-text)" }} />
          )}
          Microsoft {config.microsoft.configured ? "configured" : "not configured"}
        </span>
      </div>
    );

  const renderGoogle = () => (
    <div className="mbx-card">
      <div className="mbx-card-head">
        <span className="mbx-card-icon" style={{ background: "linear-gradient(135deg, var(--status-danger-bg), var(--status-danger-bg))", color: "#c5221f" }}>
          <Mail size={18} />
        </span>
        <h3>Google (Gmail)</h3>
        <span style={{ marginLeft: "auto" }}>
          <span className={`mbx-pill ${config?.google.configured ? "success" : "warn"}`}>
            {config?.google.configured ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
            {config?.google.configured ? "Configured" : "Setup needed"}
          </span>
        </span>
      </div>
      <p className="mbx-card-desc">
        Create the OAuth client at{" "}
        <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer">
          console.cloud.google.com → APIs &amp; Services → Credentials <ExternalLink size={11} style={{ verticalAlign: "-1px" }} />
        </a>. Enable the Gmail API and add the redirect URI below to the client.
      </p>

      <div className="mbx-row">
        <label className="mbx-field-label">Client ID</label>
        <input
          className="mbx-input mono"
          value={form.googleClientId}
          onChange={(e) => set("googleClientId", e.target.value)}
          placeholder="123456-abcdef.apps.googleusercontent.com"
        />
      </div>

      <div className="mbx-row">
        <label className="mbx-field-label">
          <KeyRound size={11} style={{ marginRight: 4, verticalAlign: "-1px" }} />
          Client Secret
          {config?.google.configured && <span className="mbx-field-hint">(leave blank to keep existing)</span>}
        </label>
        <input
          className="mbx-input"
          type="password"
          value={form.googleClientSecret}
          onChange={(e) => set("googleClientSecret", e.target.value)}
          placeholder={config?.google.configured ? "•••••••• (already saved)" : "GOCSPX-..."}
          autoComplete="new-password"
        />
      </div>

      <div className="mbx-row">
        <label className="mbx-field-label">Redirect URI</label>
        <input
          className="mbx-input mono"
          value={form.googleRedirectUri}
          onChange={(e) => set("googleRedirectUri", e.target.value)}
          placeholder="https://your-host/api/v1/mailbox/oauth/google/callback"
        />
      </div>
    </div>
  );

  const renderMicrosoft = () => (
    <div className="mbx-card">
      <div className="mbx-card-head">
        <span className="mbx-card-icon" style={{ background: "linear-gradient(135deg, var(--status-info-bg), #bfdbfe)", color: "#005a9e" }}>
          <Mail size={18} />
        </span>
        <h3>Microsoft (Outlook)</h3>
        <span style={{ marginLeft: "auto" }}>
          <span className={`mbx-pill ${config?.microsoft.configured ? "success" : "warn"}`}>
            {config?.microsoft.configured ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
            {config?.microsoft.configured ? "Configured" : "Setup needed"}
          </span>
        </span>
      </div>
      <p className="mbx-card-desc">
        Register an app at{" "}
        <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps" target="_blank" rel="noreferrer">
          Azure Portal → App registrations <ExternalLink size={11} style={{ verticalAlign: "-1px" }} />
        </a>. Add delegated permissions <code>Mail.Read</code>, <code>User.Read</code>, and <code>offline_access</code>.
      </p>

      <div className="mbx-row">
        <label className="mbx-field-label">Application (client) ID</label>
        <input
          className="mbx-input mono"
          value={form.microsoftClientId}
          onChange={(e) => set("microsoftClientId", e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
        />
      </div>

      <div className="mbx-row">
        <label className="mbx-field-label">
          <KeyRound size={11} style={{ marginRight: 4, verticalAlign: "-1px" }} />
          Client Secret
          {config?.microsoft.configured && <span className="mbx-field-hint">(leave blank to keep existing)</span>}
        </label>
        <input
          className="mbx-input"
          type="password"
          value={form.microsoftClientSecret}
          onChange={(e) => set("microsoftClientSecret", e.target.value)}
          placeholder={
            config?.microsoft.configured
              ? "•••••••• (already saved)"
              : "Client secret value (not the secret ID)"
          }
          autoComplete="new-password"
        />
      </div>

      <div className="mbx-row">
        <label className="mbx-field-label">Redirect URI</label>
        <input
          className="mbx-input mono"
          value={form.microsoftRedirectUri}
          onChange={(e) => set("microsoftRedirectUri", e.target.value)}
          placeholder="https://your-host/api/v1/mailbox/oauth/microsoft/callback"
        />
      </div>

      <div className="mbx-row">
        <label className="mbx-field-label">Tenant Authority</label>
        <input
          className="mbx-input mono"
          value={form.microsoftTenantAuthority}
          onChange={(e) => set("microsoftTenantAuthority", e.target.value)}
          placeholder='"common" (recommended) or your Azure tenant GUID'
        />
      </div>
    </div>
  );

  const renderGeneral = () => (
    <div className="mbx-card">
      <div className="mbx-card-head">
        <span className="mbx-card-icon"><Globe size={18} /></span>
        <h3>iTeach Base URL</h3>
      </div>
      <p className="mbx-card-desc">
        Used to redirect users back after OAuth completes. Falls back to the request origin if
        unset. Leave blank in local development.
      </p>
      <div className="mbx-row">
        <label className="mbx-field-label">Base URL</label>
        <input
          className="mbx-input mono"
          value={form.iteachBaseUrl}
          onChange={(e) => set("iteachBaseUrl", e.target.value)}
          placeholder="https://iteach.yourdomain.com"
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="mbx-page">
        <aside className="mbx-side">
          <h2>Mailbox</h2>
          <button type="button" className="mbx-nav-btn" disabled>
            <Inbox size={16} className="mbx-nav-icon" />
            <span className="mbx-nav-label">My Connections</span>
          </button>
          <button type="button" className="mbx-nav-btn active">
            <SettingsIcon size={16} className="mbx-nav-icon" />
            <span className="mbx-nav-label">OAuth Settings</span>
          </button>
        </aside>
        <main className="mbx-main">
          <div className="mbx-loading"><div className="mbx-spinner" /> Loading OAuth configuration…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="mbx-page">
      <aside className="mbx-side">
        <h2>Mailbox</h2>
        <button
          type="button"
          className="mbx-nav-btn"
          onClick={() => history.push("/settings/mailbox")}
        >
          <Inbox size={16} className="mbx-nav-icon" />
          <span className="mbx-nav-label">My Connections</span>
        </button>
        <button type="button" className="mbx-nav-btn active">
          <SettingsIcon size={16} className="mbx-nav-icon" />
          <span className="mbx-nav-label">OAuth Settings</span>
        </button>

        <div className="mbx-divider" />
        <h2 style={{ marginTop: 0 }}>Provider</h2>
        <button
          type="button"
          className={`mbx-nav-btn ${activeTab === "google" ? "active" : ""}`}
          onClick={() => setActiveTab("google")}
        >
          <Mail size={16} className="mbx-nav-icon" />
          <span className="mbx-nav-label">Google (Gmail)</span>
          <span className={`mbx-nav-pill ${activeTab === "google" ? "" : ""}`}>
            {config?.google.configured ? "OK" : "—"}
          </span>
        </button>
        <button
          type="button"
          className={`mbx-nav-btn ${activeTab === "microsoft" ? "active" : ""}`}
          onClick={() => setActiveTab("microsoft")}
        >
          <Mail size={16} className="mbx-nav-icon" />
          <span className="mbx-nav-label">Microsoft (Outlook)</span>
          <span className="mbx-nav-pill">
            {config?.microsoft.configured ? "OK" : "—"}
          </span>
        </button>
        <button
          type="button"
          className={`mbx-nav-btn ${activeTab === "general" ? "active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          <Globe size={16} className="mbx-nav-icon" />
          <span className="mbx-nav-label">General</span>
        </button>

        <div className="mbx-divider" />
        <button
          type="button"
          className="mbx-nav-btn"
          onClick={() => history.push("/tenant-preferences")}
          style={{ fontSize: 13, color: "var(--text-muted)" }}
          title="Back to tenant preferences hub"
        >
          <ShieldCheck size={14} className="mbx-nav-icon" />
          <span className="mbx-nav-label">Tenant Preferences</span>
        </button>
      </aside>

      <main className="mbx-main">
        <div className="mbx-header">
          <div>
            <h1>
              {activeTab === "google" && "Google OAuth Client"}
              {activeTab === "microsoft" && "Microsoft OAuth Client"}
              {activeTab === "general" && "General Settings"}
            </h1>
            <p className="mbx-subtitle">
              Configure the OAuth applications that users authenticate against when connecting their
              mailboxes. Client IDs and redirect URIs are visible; client secrets are encrypted at rest
              and never displayed after save.
            </p>
          </div>
        </div>

        {sourceBanner()}

        {error && (
          <div className="mbx-alert mbx-alert-error">
            <AlertCircle size={16} className="mbx-alert-icon" />
            <div className="mbx-alert-body">{error}</div>
            <button type="button" className="mbx-alert-close" onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {info && (
          <div className="mbx-alert mbx-alert-success">
            <CheckCircle2 size={16} className="mbx-alert-icon" />
            <div className="mbx-alert-body">{info}</div>
            <button type="button" className="mbx-alert-close" onClick={() => setInfo(null)}>&times;</button>
          </div>
        )}

        {activeTab === "google" && renderGoogle()}
        {activeTab === "microsoft" && renderMicrosoft()}
        {activeTab === "general" && renderGeneral()}

        <div className="mbx-toolbar">
          {dirty && (
            <span style={{ marginRight: "auto", fontSize: 13, color: "var(--status-warning-text)", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <AlertCircle size={14} /> Unsaved changes
            </span>
          )}
          <button
            type="button"
            className="mbx-btn mbx-btn-secondary"
            onClick={revert}
            disabled={!dirty || saving}
          >
            <RefreshCw size={14} /> Revert
          </button>
          <button
            type="button"
            className="mbx-btn mbx-btn-secondary"
            onClick={load}
            disabled={saving}
          >
            Reload
          </button>
          <button
            type="button"
            className="mbx-btn mbx-btn-primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default MailboxAdminPage;
